import React from "react";
import { Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FONTS } from "./constants";
import { SliderRow } from "./SliderRow";

export function LayerInspector({ selectedIds, activeLayer, updateLayer, deleteSelected }) {
  return (
    <div className="bg-white p-5 rounded-lg border hairline h-fit">
      <div className="uppercase text-[10px] tracking-[0.3em] font-bold mb-4" style={{color: "var(--secondary)"}}>Selected layer</div>
      {selectedIds.size > 1 ? (
        <div data-testid="multi-select-panel" className="space-y-4 text-sm">
          <div className="rounded-md p-3" style={{background: "rgba(197,160,89,0.08)"}}>
            <div className="font-semibold" style={{color: "var(--primary)"}}>{selectedIds.size} layers selected</div>
            <div className="text-xs text-[color:var(--text-muted)] mt-1 leading-relaxed">
              Drag any one of them and they'll all follow. Use arrow keys to nudge the whole group, or pinch to resize them together on mobile.
            </div>
          </div>
          <Button data-testid="delete-selected-btn" onClick={deleteSelected} variant="outline" size="sm" className="w-full rounded-full text-rose-700 hover:text-rose-800">
            <Trash2 size={14} className="mr-2"/> Delete all selected
          </Button>
        </div>
      ) : !activeLayer ? (
        <div className="text-xs text-[color:var(--text-muted)]">Add or select a layer to edit.</div>
      ) : activeLayer.type === "text" ? (
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Text</Label>
            <Input data-testid="layer-text-input" value={activeLayer.text} onChange={(e) => updateLayer(activeLayer.id, { text: e.target.value })}/>
          </div>
          <div>
            <Label className="text-xs">Font</Label>
            <Select value={activeLayer.font} onValueChange={(v) => updateLayer(activeLayer.id, { font: v })}>
              <SelectTrigger data-testid="layer-font-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {FONTS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <SliderRow
            testid="layer-size-slider"
            label="Size" unit="px"
            value={activeLayer.size} min={12} max={140} step={1}
            onChange={(v) => updateLayer(activeLayer.id, { size: v })}
          />
          <SliderRow
            testid="layer-rotation-slider"
            label="Rotation" unit="°"
            value={activeLayer.rotation} min={-180} max={180} step={1}
            onChange={(v) => updateLayer(activeLayer.id, { rotation: v })}
          />
          <div>
            <Label className="text-xs">Color</Label>
            <input data-testid="layer-color-input" type="color" value={activeLayer.color} onChange={(e) => updateLayer(activeLayer.id, { color: e.target.value })} className="w-full h-9 rounded-md border hairline"/>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <SliderRow
            label="Width" unit="px"
            value={activeLayer.width} min={40} max={500} step={2}
            onChange={(v) => updateLayer(activeLayer.id, { width: v })}
          />
          <SliderRow
            label="Height" unit="px"
            value={activeLayer.height} min={40} max={500} step={2}
            onChange={(v) => updateLayer(activeLayer.id, { height: v })}
          />
          <SliderRow
            label="Rotation" unit="°"
            value={activeLayer.rotation} min={-180} max={180} step={1}
            onChange={(v) => updateLayer(activeLayer.id, { rotation: v })}
          />
          <SliderRow
            label="Opacity" unit="%"
            value={Math.round(activeLayer.opacity * 100)} min={20} max={100} step={1}
            onChange={(v) => updateLayer(activeLayer.id, { opacity: v/100 })}
          />
          <div className="pt-4 border-t hairline space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Engrave style</Label>
              <button
                data-testid="engrave-toggle"
                role="switch"
                aria-checked={!!activeLayer.engraved}
                onClick={() => updateLayer(activeLayer.id, { engraved: !activeLayer.engraved })}
                className={`relative w-11 h-6 rounded-full transition-colors ${activeLayer.engraved ? "bg-[color:var(--secondary)]" : "bg-neutral-300"}`}
              >
                <span aria-hidden className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md ring-1 ring-black/5 transition-transform duration-200 ${activeLayer.engraved ? "translate-x-5" : "translate-x-0"}`}/>
              </button>
            </div>
            <p className="text-[10px] text-[color:var(--text-muted)] leading-relaxed">Simulates a laser burn into the material. Best for pet portraits, photos, and silhouettes.</p>
            {activeLayer.engraved && (
              <>
                <SliderRow
                  testid="burn-depth"
                  label="Burn depth" unit="×"
                  value={Number(((activeLayer.contrast ?? 1.6)).toFixed(1))} min={1.0} max={3.0} step={0.1}
                  onChange={(v) => updateLayer(activeLayer.id, { contrast: v })}
                />
                <SliderRow
                  testid="char-tone"
                  label="Char tone" unit="%"
                  value={Math.round((activeLayer.brightness ?? 0.9) * 100)} min={40} max={110} step={1}
                  onChange={(v) => updateLayer(activeLayer.id, { brightness: v/100 })}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
